import { createAsyncThunk } from '@reduxjs/toolkit';
import api, { handleApiError } from 'state/api';

const unwrapApiData = (response, rejectWithValue) => {
  const body = response?.data;
  if (!body?.success) {
    return rejectWithValue({
      message: body?.message || body?.error || 'Request failed',
    });
  }
  return body.data ?? body;
};

export const getOptimizerSettingsApi = createAsyncThunk(
  'listingOptimizer/getSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('listing-optimizer/settings');
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('getOptimizerSettingsApi', error, rejectWithValue);
    }
  },
);

export const updateOptimizerSettingsApi = createAsyncThunk(
  'listingOptimizer/updateSettings',
  async (settings, { rejectWithValue }) => {
    try {
      const response = await api.put('listing-optimizer/settings', settings);
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('updateOptimizerSettingsApi', error, rejectWithValue);
    }
  },
);

export const getOptimizerOverviewApi = createAsyncThunk(
  'listingOptimizer/getOverview',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('listing-optimizer/overview');
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('getOptimizerOverviewApi', error, rejectWithValue);
    }
  },
);

export const getOptimizerProcessStatusApi = createAsyncThunk(
  'listingOptimizer/getProcessStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('listing-optimizer/process-status');
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('getOptimizerProcessStatusApi', error, rejectWithValue);
    }
  },
);

export const getOptimizerMappingInfoApi = createAsyncThunk(
  'listingOptimizer/getMappingInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('listing-optimizer/mapping');
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('getOptimizerMappingInfoApi', error, rejectWithValue);
    }
  },
);

export const uploadOptimizerMappingApi = createAsyncThunk(
  'listingOptimizer/uploadMapping',
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('listing-optimizer/mapping/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('uploadOptimizerMappingApi', error, rejectWithValue);
    }
  },
);

export const startOptimizerRunApi = createAsyncThunk(
  'listingOptimizer/startRun',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post('listing-optimizer/run', payload);
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('startOptimizerRunApi', error, rejectWithValue);
    }
  },
);

export const stopOptimizerRunApi = createAsyncThunk(
  'listingOptimizer/stopRun',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('listing-optimizer/run/stop');
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('stopOptimizerRunApi', error, rejectWithValue);
    }
  },
);

export const getOptimizerScopeCountApi = createAsyncThunk(
  'listingOptimizer/getScopeCount',
  async (
    { scopeMode, storeIds, supplierIds, manualSkus, limit, includeOptimizedRerun, includeHaltedRerun },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams();
      if (scopeMode) params.append('scopeMode', scopeMode);
      if (storeIds?.length) params.append('storeIds', storeIds.join(','));
      if (supplierIds?.length) params.append('supplierIds', supplierIds.join(','));
      if (manualSkus?.length) params.append('manualSkus', manualSkus.join(','));
      if (limit != null) params.append('limit', String(limit));
      if (includeOptimizedRerun) params.append('includeOptimizedRerun', 'true');
      if (includeHaltedRerun) params.append('includeHaltedRerun', 'true');
      const response = await api.get(`listing-optimizer/scope-count?${params.toString()}`);
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('getOptimizerScopeCountApi', error, rejectWithValue);
    }
  },
);

export const downloadOptimizerRunReportCsvApi = createAsyncThunk(
  'listingOptimizer/downloadRunReportCsv',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('listing-optimizer/run-report/download', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `listing-optimizer-run-${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { downloaded: true };
    } catch (error) {
      return handleApiError('downloadOptimizerRunReportCsvApi', error, rejectWithValue);
    }
  },
);

export const lookupOptimizerSkuApi = createAsyncThunk(
  'listingOptimizer/lookupSku',
  async (canonicalSku, { rejectWithValue }) => {
    try {
      const response = await api.get('listing-optimizer/sku-lookup', {
        params: { canonicalSku },
      });
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('lookupOptimizerSkuApi', error, rejectWithValue);
    }
  },
);
